import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LogoutIcon } from "../../assets/sidebarIcon/icons";
import {
  ArrowLeftRight,
  Box,
  Building2,
  FileText,
  LayoutGrid,
  ListChecks,
  TrendingDown,
  TrendingUp,
  Cuboid,
} from "lucide-react";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi2";
import JDLogo from '../../assets/JDLogo.png';
import JDSmallLogo from '../../assets/jdSmallLogo.png';
import { Sidebar } from './Sidebar';
import { warehouseService } from '../../services/api';
import WarehouseDetailsModal from './WarehouseDetailsModal';
import { useAuth } from '../../context/AuthContext';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const buttonRefs = useRef({});
  const [warehousePopupOpen, setWarehousePopupOpen] = useState(false);
  const [warehouseDetails, setWarehouseDetails] = useState(null);
  const [warehouseLoading, setWarehouseLoading] = useState(false);
  const [warehouseError, setWarehouseError] = useState(null);

  const handleLogout = () => {
    setWarehouseDisplayName('Warehouse');
    setWarehouseDetails(null);
    logout();
    toast.success('Logged out successfully');
    navigate('/login', { replace: true });
  };

  const handleWarehouseClick = async () => {
    setWarehousePopupOpen(true);
    setWarehouseLoading(true);
    setWarehouseError(null);
    try {
      const res = await warehouseService.getMe();
      setWarehouseDetails(res.data || res);
    } catch (err) {
      setWarehouseError(err?.message || 'Failed to load warehouse details');
      setWarehouseDetails(null);
    } finally {
      setWarehouseLoading(false);
    }
  };

  function getInitials(name) {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }

  const loadUser = () => {
    if (typeof localStorage === 'undefined') return;
    const storedUser = localStorage.getItem('user');
    const parsedUser = storedUser ? (() => {
      try {
        return JSON.parse(storedUser);
      } catch {
        return null;
      }
    })() : null;

    if (parsedUser) {
      const name =
        parsedUser?.warehouse?.name ||
        parsedUser?.warehouseName ||
        localStorage.getItem('warehouseName') ||
        'Warehouse';
      setWarehouseDisplayName(name);
      return;
    }
    setWarehouseDisplayName(localStorage.getItem('warehouseName') || 'Warehouse');
  };

  const [warehouseDisplayName, setWarehouseDisplayName] = useState(() => {
    if (typeof localStorage === 'undefined') return 'Warehouse';
    const storedUser = localStorage.getItem('user');
    const parsedUser = storedUser ? (() => {
      try {
        return JSON.parse(storedUser);
      } catch {
        return null;
      }
    })() : null;
    if (parsedUser?.warehouse?.name) return parsedUser.warehouse.name;
    return localStorage.getItem('warehouseName') || 'Warehouse';
  });

  useEffect(() => {
    loadUser();
    const syncUser = () => {
      loadUser();
    };
    window.addEventListener('storage', syncUser);
    return () => {
      window.removeEventListener('storage', syncUser);
    };
  }, []);

  const initials = getInitials(warehouseDisplayName);
  
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutGrid, path: '/' },
    { id: 'company', label: 'Company Management', icon: Building2, path: '/company' },
    { id: 'products', label: 'Product Master', icon: Box, path: '/products' },
    { id: 'inward', label: 'Inward Management', icon: TrendingDown, path: '/inward' },
    { id: 'bins', label: 'Bin Management', icon: Cuboid, path: '/bins' },
    { id: 'putaway', label: 'Putaway Management', icon: ListChecks, path: '/putaway' },
    { id: 'outward', label: 'Outward Management', icon: TrendingUp, path: '/outward' },
    { id: 'returns', label: 'Returns Management', icon: ArrowLeftRight, path: '/returns' },
    // { id: 'inventory', label: 'Inventory Management', icon: MdInventory, path: '/inventory' },
    { id: 'reports', label: 'Reports', icon: FileText, path: '/reports' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'row', height: '100vh', width: '100vw', backgroundColor: '#f9fafb', overflow: 'hidden', position: 'relative' }}>
      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-20"
          onClick={() => setIsSidebarOpen(false)}
          style={{ zIndex: 20 }}
        />
      )}
      {/* Left Sidebar */}
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        hoveredItem={hoveredItem}
        setHoveredItem={setHoveredItem}
        tooltipPos={tooltipPos}
        setTooltipPos={setTooltipPos}
        buttonRefs={buttonRefs}
        navItems={navItems}
        location={location}
        navigate={navigate}
      />
      {/* Main Content Area */}
      <main className="flex-1 flex flex-col overflow-hidden md:ml-5 md:mt-5 md:mb-5 relative" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', marginLeft: '20px', marginTop: '20px', marginBottom: '20px', position: 'relative' }}>
        {/* Header */}
        <header className="mt-5">
  <div className="w-full px-6 md:px-6 lg:px-8">
    
    <div className="rounded-[28px] border border-[#F5EFEB66] bg-white px-6 py-3 shadow-sm flex items-center justify-between">
      
      {/* LEFT SIDE */}
      <div className="hidden md:flex items-center">
        <div
          style={{
            height: '36px',
            paddingLeft: '16px',
            paddingRight: '16px',
            border: '1px solid #E5E7EB',
            borderRadius: '9999px',
            backgroundColor: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span
            style={{
              color: '#111827',
              fontSize: '16px',
              fontWeight: '600',
            }}
          >
            WMS Portal
          </span>
        </div>
      </div>

      {/* MOBILE MENU */}
      <div className="md:hidden block">
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      </div>

      {/* RIGHT SIDE */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleWarehouseClick}
          className="flex items-center gap-2 md:gap-3 cursor-pointer rounded-xl hover:bg-gray-50 active:scale-[0.99] transition p-1 pr-2"
        >
          <div
            style={{
              width: '44px',
              height: '44px',
              background: '#E6F4EA',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#15803D',
              fontSize: '17px',
              fontWeight: '600',
              border: '1.5px solid #15803D',
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            }}
          >
            {initials}
          </div>

          <div className="hidden md:flex flex-col text-left">
            <p
              style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#111827',
                margin: 0,
              }}
            >
              {warehouseDisplayName}
            </p>
            <p
              style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#9CA3AF',
                margin: 0,
              }}
            >
              Warehouse Account
            </p>
          </div>
        </button>

        <button
          type="button"
          aria-label="Logout"
          onClick={handleLogout}
          className="flex items-center justify-center bg-white border border-[#188742] rounded-[12px] cursor-pointer transition duration-[180ms] hover:bg-green-50"
          style={{ width: 44, height: 44 }}
        >
          <LogoutIcon size={20} color="#188742" />
        </button>
      </div>

    </div>
  </div>
</header>

        <WarehouseDetailsModal
          open={warehousePopupOpen}
          onClose={() => {
            setWarehousePopupOpen(false);
            setWarehouseDetails(null);
            setWarehouseError(null);
          }}
          loading={warehouseLoading}
          error={warehouseError}
          data={warehouseDetails}
        />

        {/* Page Content - overflow auto so content can scroll when sidebar open */}
        <div className="flex-1 overflow-auto p-2 md:p-4 bg-[#f9fafb] min-w-0" style={{ flex: 1, overflow: 'auto', padding: '1rem', backgroundColor: '#f9fafb', minWidth: 0 }}>
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;